import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useHistory } from "react-router-dom";
import type { Location } from "history";
import { useAuth } from "../../contexts/UserContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import api from "../../services/api";
import toast from "react-hot-toast";
import { toastDefsError } from "../../config/toastConfig";
import PlanosDetailSkeleton from "../../components/Ui/PlanosDetailSkeleton";
import ModalConfirmDiscard from "../../components/ModalConfirmDiscard";
import { applyMoneyMask } from "../../utils/masks";
import { ArrowLeft, Settings, DollarSign, CreditCard, Save, HelpCircle, Package } from "lucide-react";
import AbaHistoricoPlano from "../../components/Planos/AbaHistorico/AbaHistoricoPlano";
import { comum } from "../../styles/Themes/light";
import Tooltip from "../../components/Ui/Tooltip";
import { formatServicoColuna } from "../../utils/servicoMapperPlanos";
import InputMoney from "../../components/form/InputMoney";
import { PrimaryButton, ActionButton } from "../../components/ListaDemandas/CabecalhoListaDemanda/styles";
import {
  Page,
  HeaderSticky,
  HeaderInner,
  HeaderLeft,
  TabNavSticky,
  TabNavInner,
  TabNav,
  TabButton,
  Content,
  ContentCol,
  LoadingWrap,
  ErrorWrap,
  ErrorMessage,
  RetryButton,
  Card,
  CardHeader,
  CardHeaderLeft,
  CardTitleWrap,
  CardTitle,
  StatusBadge,
  ButtonGroup,
  ConfigGrid,
  FieldCell,
  ConfigErrorsSummary,
  FieldError,
  FieldLabel,
  FieldLabelDiv,
  InputDefault,
  ValueRow,
  ValueText,
  ToggleRow,
  ToggleTrack,
  ToggleThumb,
  ToggleLabel,
  ViewValueRow,
  PricingCardHeader,
  PricingCardHeaderLeft,
  PricingIconWrap,
  PricingTitleWrap,
  PricingTitle,
  HelpIconWrap,
  TableWrap,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  ModalidadeBadge,
  PricingCellWithError,
  InputSmall,
  TotalValue,
  ConsultaBadge,
  ToggleSwitch,
  ToggleSwitchThumb,
  InputMoneyWrap,
  InputMoneyCell,
  MobileCardList,
  MobileServiceCard,
  MobileCardTotal,
  MobileCardEditRow,
  EmptyPricingState,
  EmptyPricingIcon,
  EmptyPricingTitle,
  EmptyPricingMessage,
  EmptyPricingLink,
} from "./styles";

type TabType = "principal" | "historico";

interface PlanoItem {
  id_plano_solicitante_template: number;
  nome: string;
  quantidade_creditos: number | null;
  valor_plano: number | null;
  valor_credito?: number | null;
  valor_onboarding: number | null;
  validade_creditos_dias: number | null;
  status: string;
  ordem_servico?: number;
}

interface ServicoItem {
  id_plano_servico_template: number;
  servico: string | null;
  area_direito: string | null;
  tipo_audiencia?: string | null;
  modalidade?: string | null;
  profissional?: string | null;
  creditos_padrao: number | null;
  valor_credito: number | null;
  modo_precificacao: string | null;
  descricao?: string | null;
}

interface PlanWithPricing {
  plano: PlanoItem;
  precificacao: ServicoItem[];
}

function isPlanoOS(plano: PlanoItem): boolean {
  return Boolean(plano.ordem_servico === 1);
}

function formatBRL(value: number | null | undefined): string {
  if (value == null) return "-";
  return applyMoneyMask(String(value));
}

export default function PlanosDetail() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { user, setPageType } = useAuth();
  const planId = id ? parseInt(id, 10) : 0;

  useDocumentTitle({ title: "Configuração de Planos" });

  const { isCollapsed: sidebarCollapsed } = useSidebar();
  const isAdmin = user?.profile?.toLowerCase() === "admin";
  const isOperador = user?.profile?.toLowerCase() === "operador";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlanWithPricing | null>(null);
  const [editingConfig, setEditingConfig] = useState(false);
  const [editingPricing, setEditingPricing] = useState(false);
  const [draftConfig, setDraftConfig] = useState<Partial<PlanoItem>>({});
  const [draftPricing, setDraftPricing] = useState<ServicoItem[]>([]);
  const [activeTab, setActiveTab] = useState<TabType>("principal");
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [configErrors, setConfigErrors] = useState<Record<string, string>>({});
  const [pricingErrors, setPricingErrors] = useState<Record<number, { creditos_padrao?: string; valor_credito?: string }>>({});
  const unblockRef = useRef<(() => void) | null>(null);

  const isOS = data?.plano ? isPlanoOS(data.plano) : false;
  const hasUnsavedChanges = Boolean(data && isAdmin && (editingConfig || editingPricing));

  const fetchPlanWithPricing = useCallback(async (idPlan: number) => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get(`/plano-solicitante-template/${idPlan}?withPricing=true`);
      if (res.data?.status !== "success") throw new Error(res.data?.message || "Erro ao carregar plano");
      const result = res.data?.result;
      if (!result?.plano) throw new Error("Plano não encontrado");
      setData({ plano: result.plano, precificacao: result.precificacao || [] });
      setDraftConfig({});
      setDraftPricing(result.precificacao || []);
      setEditingConfig(false);
      setEditingPricing(false);
    } catch (e: any) {
      setError("Ocorreu um erro ao carregar os dados.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && !isOperador) {
      history.replace("/demandas");
      return;
    }
  }, [isAdmin, isOperador, history]);

  useEffect(() => {
    // Define pageType como 'vazio' para remover o Header do Layout
    setPageType('vazio');
    return () => {
      // Restaura o pageType quando o componente desmontar
      setPageType('default');
    };
  }, [setPageType]);

  useEffect(() => {
    if (planId && !isNaN(planId)) fetchPlanWithPricing(planId);
  }, [planId, fetchPlanWithPricing]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const unblock = history.block((nextLoc: Location) => {
      setPendingLocation(nextLoc);
      setShowConfirmDiscard(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [hasUnsavedChanges, history]);

  const displayList = useMemo(() => {
    const precificacao = data?.precificacao ?? [];
    const list = editingPricing ? draftPricing : precificacao;
    return [...list].sort((a, b) =>
      formatServicoColuna(a.area_direito, a.servico, a.tipo_audiencia).localeCompare(
        formatServicoColuna(b.area_direito, b.servico, b.tipo_audiencia),
        "pt-BR"
      )
    );
  }, [data?.precificacao, editingPricing, draftPricing]);

  const handleConfirmDiscard = () => {
    const next = pendingLocation;
    setShowConfirmDiscard(false);
    setPendingLocation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (next) history.push(next);
  };

  const handleCloseConfirmDiscard = () => {
    setShowConfirmDiscard(false);
    setPendingLocation(null);
  };

  const handleSaveConfig = async () => {
    if (!planId || !isAdmin || !data) return;
    const e: Record<string, string> = {};
    const qtdCreditos = draftConfig.quantidade_creditos ?? data.plano.quantidade_creditos ?? 0;
    const valorPlano = draftConfig.valor_plano ?? data.plano.valor_plano ?? 0;
    const validadeDias = draftConfig.validade_creditos_dias ?? data.plano.validade_creditos_dias ?? 0;
    if (!isOS) {
      if (qtdCreditos == null || qtdCreditos <= 0) {
        e.quantidade_creditos = "Informe uma quantidade válida de créditos (maior que zero).";
      }
      if (valorPlano == null || valorPlano <= 0) {
        e.valor_plano = "O valor do plano deve ser maior que zero.";
      }
      if (validadeDias == null || validadeDias <= 0) {
        e.validade_creditos_dias = "Validade (dias) deve ser maior que zero.";
      }
    }
    setConfigErrors(e);
    if (Object.keys(e).length > 0) return;
    try {
      await api.patch(`/plano-solicitante-template/${planId}/configuracoes`, {
        quantidade_creditos: draftConfig.quantidade_creditos ?? data.plano.quantidade_creditos,
        valor_plano: draftConfig.valor_plano ?? data.plano.valor_plano,
        valor_onboarding: draftConfig.valor_onboarding !== undefined ? draftConfig.valor_onboarding : data.plano.valor_onboarding,
        validade_creditos_dias: draftConfig.validade_creditos_dias ?? data.plano.validade_creditos_dias,
        status: draftConfig.status ?? data.plano.status,
      });
      toast.success("Alterações salvas com sucesso.");
      setEditingConfig(false);
      setDraftConfig({});
      setConfigErrors({});
      fetchPlanWithPricing(planId);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || "Erro ao salvar.", toastDefsError);
    }
  };

  const handleSavePricing = async () => {
    if (!planId || !isAdmin) return;
    const errors: Record<number, { creditos_padrao?: string; valor_credito?: string }> = {};
    for (const row of draftPricing) {
      const sobConsulta = row.modo_precificacao === "sob_consulta" || isOS;
      if (sobConsulta) continue;
      const cred = row.creditos_padrao ?? 0;
      const val = row.valor_credito ?? 0;
      if (cred <= 0) {
        errors[row.id_plano_servico_template] = {
          ...errors[row.id_plano_servico_template],
          creditos_padrao: "Créditos deve ser maior que zero.",
        };
      }
      if (val <= 0) {
        errors[row.id_plano_servico_template] = {
          ...errors[row.id_plano_servico_template],
          valor_credito: "Valor/Crédito deve ser maior que zero.",
        };
      }
    }
    if (Object.keys(errors).length > 0) {
      setPricingErrors(errors);
      return;
    }
    setPricingErrors({});
    try {
      const items = draftPricing.map((s) => ({
        id_plano_servico_template: s.id_plano_servico_template,
        creditos_padrao: s.creditos_padrao,
        valor_credito: s.valor_credito,
        modo_precificacao: s.modo_precificacao,
      }));
      await api.patch(`/plano-solicitante-template/${planId}/precificacao`, { items });
      toast.success("Alterações salvas com sucesso.");
      setEditingPricing(false);
      fetchPlanWithPricing(planId);
    } catch (e: any) {
      toast.error(e.response?.data?.message || e.message || "Erro ao salvar.", toastDefsError);
    }
  };

  if (!isAdmin && !isOperador) return null;

  if (loading && !data) {
    return (
      <LoadingWrap>
        <PlanosDetailSkeleton />
      </LoadingWrap>
    );
  }

  if (error && !data) {
    return (
      <ErrorWrap>
        <ErrorMessage>{error}</ErrorMessage>
        <RetryButton type="button" onClick={() => planId && fetchPlanWithPricing(planId)}>
          Tentar novamente
        </RetryButton>
      </ErrorWrap>
    );
  }

  if (!data) return null;

  const plano = data.plano;
  const config = editingConfig ? { ...plano, ...draftConfig } : plano;

  const valorCreditoComputado =
    (config.quantidade_creditos != null && config.quantidade_creditos > 0 && config.valor_plano != null)
      ? config.valor_plano / config.quantidade_creditos
      : null;
  const valorCreditoPlano = editingConfig ? valorCreditoComputado : (config.valor_credito ?? valorCreditoComputado);

  const handleToggleSobConsulta = (idPlanoServico: number) => {
    setDraftPricing((prev) =>
      prev.map((i) => {
        if (i.id_plano_servico_template !== idPlanoServico) return i;
        if (i.modo_precificacao === "sob_consulta") {
          return {
            ...i,
            modo_precificacao: "fixo_em_creditos",
            valor_credito: valorCreditoPlano ?? i.valor_credito,
          };
        }
        return { ...i, modo_precificacao: "sob_consulta" };
      })
    );
  };

  return (
    <Page>
      <HeaderSticky>
        <HeaderInner>
          <HeaderLeft>
            <div className="voltar-container" onClick={() => history.push("/planos")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && history.push("/planos")} aria-label="Voltar para planos">
              <ArrowLeft size={20} />
              <span>Voltar</span>
            </div>
            <div className="divider" />
            <Tooltip content={`Plano ${plano.nome ?? ""}`} longContent>
              <h1>Plano {plano.nome}</h1>
            </Tooltip>
          </HeaderLeft>
        </HeaderInner>
      </HeaderSticky>

      <TabNavSticky $sidebarCollapsed={sidebarCollapsed}>
        <TabNavInner>
          <TabNav>
            <TabButton type="button" onClick={() => setActiveTab("principal")} $active={activeTab === "principal"}>
              Principal
            </TabButton>
            <TabButton type="button" onClick={() => setActiveTab("historico")} $active={activeTab === "historico"}>
              Histórico
            </TabButton>
          </TabNav>
        </TabNavInner>
      </TabNavSticky>

      <Content $sidebarCollapsed={sidebarCollapsed}>
        <ContentCol>

      {activeTab === "principal" && (
        <>
      <Card>
        <CardHeader>
          <CardHeaderLeft>
            <Settings size={20} color={comum.colors.interfaceGray400} style={{ flexShrink: 0 }} />
            <CardTitleWrap>
              <CardTitle>
                <Tooltip content={`Configurações gerais - ${plano.nome}`} longContent>
                  <span className="card-title-text">Configurações gerais - {plano.nome}</span>
                </Tooltip>
                <StatusBadge $ativo={plano.status === "ativo"}>{plano.status === "ativo" ? "Ativo" : "Inativo"}</StatusBadge>
              </CardTitle>
            </CardTitleWrap>
          </CardHeaderLeft>
          {isAdmin && !isOS && (
            editingConfig ? (
              <ButtonGroup>
                <ActionButton type="button" onClick={() => { setEditingConfig(false); setDraftConfig({}); setConfigErrors({}); }}>
                  Cancelar
                </ActionButton>
                <PrimaryButton type="button" onClick={handleSaveConfig} className="planos-detail-save-btn">
                  <Save size={18} />
                  <span className="btn-label-full">Salvar alterações</span>
                  <span className="btn-label-mobile">Salvar</span>
                </PrimaryButton>
              </ButtonGroup>
            ) : (
              <PrimaryButton type="button" onClick={() => { setDraftConfig({ ...plano }); setEditingConfig(true); setConfigErrors({}); }}>
                Configurar plano
              </PrimaryButton>
            )
          )}
        </CardHeader>
        {editingConfig ? (
            <>
            {Object.keys(configErrors).length > 0 && (
              <ConfigErrorsSummary role="alert">
                Corrija os campos marcados abaixo antes de salvar.
              </ConfigErrorsSummary>
            )}
            <ConfigGrid>
              <FieldCell className="field-creditos" $hasError={Boolean(configErrors.quantidade_creditos)}>
                <FieldLabel>CRÉDITOS DO PLANO</FieldLabel>
                <InputDefault
                  type="number"
                  min={1}
                  value={config.quantidade_creditos ?? ""}
                  onChange={(e) => {
                    setDraftConfig((c) => ({ ...c, quantidade_creditos: e.target.value ? parseInt(e.target.value, 10) : undefined }));
                    if (configErrors.quantidade_creditos) setConfigErrors((prev) => ({ ...prev, quantidade_creditos: "" }));
                  }}
                />
                {configErrors.quantidade_creditos ? <FieldError>{configErrors.quantidade_creditos}</FieldError> : null}
              </FieldCell>
              <FieldCell className="field-valor-plano" $hasError={Boolean(configErrors.valor_plano)}>
                <FieldLabel>VALOR DO PLANO</FieldLabel>
                <InputMoneyWrap>
                  <InputMoney
                    title=""
                    value={config.valor_plano != null && !isNaN(Number(config.valor_plano)) ? Number(config.valor_plano).toFixed(2) : "0.00"}
                    onChangeText={(v) => {
                      setDraftConfig((c) => ({ ...c, valor_plano: v ? parseFloat(v) : undefined }));
                      if (configErrors.valor_plano) setConfigErrors((prev) => ({ ...prev, valor_plano: "" }));
                    }}
                    placeholder="R$ 0,00"
                  />
                </InputMoneyWrap>
                {configErrors.valor_plano ? <FieldError>{configErrors.valor_plano}</FieldError> : null}
              </FieldCell>
              <FieldCell className="field-valor-credito">
                <FieldLabel>VALOR / CRÉDITO</FieldLabel>
                <ValueRow>
                  <DollarSign size={22} color="#D97706" style={{ flexShrink: 0 }} />
                  <ValueText>{isOS ? "-" : formatBRL(valorCreditoPlano)}</ValueText>
                </ValueRow>
              </FieldCell>
              <FieldCell className="field-valor-onboard">
                <FieldLabel>VALOR ONBOARDING</FieldLabel>
                <InputMoneyWrap>
                  <InputMoney
                    title=""
                    value={config.valor_onboarding != null && !isNaN(Number(config.valor_onboarding)) ? Number(config.valor_onboarding).toFixed(2) : "0.00"}
                    onChangeText={(v) => setDraftConfig((c) => ({ ...c, valor_onboarding: v ? parseFloat(v) : null }))}
                    placeholder="R$ 0,00"
                  />
                </InputMoneyWrap>
              </FieldCell>
              <FieldCell className="field-validade" $hasError={Boolean(configErrors.validade_creditos_dias)}>
                <FieldLabel>VALIDADE (DIAS)</FieldLabel>
                <InputDefault
                  type="number"
                  min={1}
                  value={config.validade_creditos_dias ?? ""}
                  onChange={(e) => {
                    setDraftConfig((c) => ({ ...c, validade_creditos_dias: e.target.value ? parseInt(e.target.value, 10) : undefined }));
                    if (configErrors.validade_creditos_dias) setConfigErrors((prev) => ({ ...prev, validade_creditos_dias: "" }));
                  }}
                />
                {configErrors.validade_creditos_dias ? <FieldError>{configErrors.validade_creditos_dias}</FieldError> : null}
              </FieldCell>
            </ConfigGrid>
            <ToggleRow>
              <ToggleTrack
                type="button"
                role="switch"
                aria-checked={(config.status ?? "ativo") === "ativo"}
                $on={(config.status ?? "ativo") === "ativo"}
                onClick={() => setDraftConfig((c) => ({ ...c, status: (c.status ?? "ativo") === "ativo" ? "inativo" : "ativo" }))}
              >
                <ToggleThumb $on={(config.status ?? "ativo") === "ativo"} />
              </ToggleTrack>
              <ToggleLabel>PLANO ATIVO</ToggleLabel>
            </ToggleRow>
          </>
        ) : (
          <ConfigGrid>
            <FieldCell className="field-creditos">
              <FieldLabelDiv>CRÉDITOS DO PLANO</FieldLabelDiv>
              <ViewValueRow>
                <CreditCard size={18} color={comum.colors.primaryRed400} />
                <ValueText>{isOS ? "-" : (config.quantidade_creditos ?? "-")}</ValueText>
              </ViewValueRow>
            </FieldCell>
            <FieldCell className="field-valor-plano">
              <FieldLabelDiv>VALOR DO PLANO</FieldLabelDiv>
              <ViewValueRow>
                <DollarSign size={22} color={comum.colors.interfaceSuccess} style={{ flexShrink: 0 }} />
                <ValueText>{isOS ? "-" : formatBRL(config.valor_plano)}</ValueText>
              </ViewValueRow>
            </FieldCell>
            <FieldCell className="field-valor-credito">
              <FieldLabelDiv>VALOR / CRÉDITO</FieldLabelDiv>
              <ViewValueRow>
                <DollarSign size={22} color="#D97706" style={{ flexShrink: 0 }} />
                <ValueText>{isOS ? "-" : formatBRL(valorCreditoPlano)}</ValueText>
              </ViewValueRow>
            </FieldCell>
            <FieldCell className="field-valor-onboard">
              <FieldLabelDiv>VALOR ONBOARDING</FieldLabelDiv>
              <ViewValueRow>
                <DollarSign size={22} color={comum.colors.primaryBlue100} style={{ flexShrink: 0 }} />
                <ValueText>{isOS ? "-" : formatBRL(config.valor_onboarding)}</ValueText>
              </ViewValueRow>
            </FieldCell>
            <FieldCell className="field-validade">
              <FieldLabelDiv>VALIDADE (DIAS)</FieldLabelDiv>
              <ValueText>{isOS ? "-" : (config.validade_creditos_dias != null ? `${config.validade_creditos_dias} dias` : "-")}</ValueText>
            </FieldCell>
          </ConfigGrid>
        )}
      </Card>

      <Card>
        <PricingCardHeader>
          <PricingCardHeaderLeft>
            <PricingIconWrap>
              <DollarSign size={50} color={comum.colors.interfaceGray400} />
            </PricingIconWrap>
            <PricingTitleWrap>
              <PricingTitle>Precificação de serviços</PricingTitle>
              <HelpIconWrap as="span">
                <Tooltip content="Serviços não listados são considerados Sob Consulta" centerAboveTrigger>
                  <HelpCircle size={16} color={comum.colors.interfaceGray400} />
                </Tooltip>
              </HelpIconWrap>
            </PricingTitleWrap>
          </PricingCardHeaderLeft>
          {isAdmin && !isOS && (
            editingPricing ? (
              <ButtonGroup>
                <ActionButton type="button" onClick={() => setEditingPricing(false)}>
                  Cancelar
                </ActionButton>
                <PrimaryButton type="button" onClick={handleSavePricing} className="planos-detail-save-btn">
                  <Save size={18} />
                  <span className="btn-label-full">Salvar alterações</span>
                  <span className="btn-label-mobile">Salvar</span>
                </PrimaryButton>
              </ButtonGroup>
            ) : (
              <PrimaryButton type="button" onClick={() => { setEditingPricing(true); setDraftPricing([...(data?.precificacao ?? [])]); setPricingErrors({}); }}>
                Editar serviços
              </PrimaryButton>
            )
          )}
        </PricingCardHeader>
        <TableWrap>
          {displayList.length === 0 ? (
            <EmptyPricingState>
              <EmptyPricingIcon>
                <Package size={48} strokeWidth={1.2} />
              </EmptyPricingIcon>
              <EmptyPricingTitle>Nenhum serviço cadastrado</EmptyPricingTitle>
              <EmptyPricingMessage>
                Nenhum serviço foi criado no template. Acesse a página de Serviços Template para cadastrar os serviços e vinculá-los aos planos.
              </EmptyPricingMessage>
              <EmptyPricingLink type="button" onClick={() => history.push("/servicos-template")}>
                Ir para Serviços Template
              </EmptyPricingLink>
            </EmptyPricingState>
          ) : (
            <>
          <MobileCardList>
            {displayList.map((s) => {
              const sobConsulta = s.modo_precificacao === "sob_consulta" || isOS;
              const cred = sobConsulta ? null : (s.creditos_padrao ?? 0);
              const val = sobConsulta ? null : (s.valor_credito ?? 0);
              const total = cred != null && val != null ? cred * val : null;
              return (
                <MobileServiceCard key={s.id_plano_servico_template}>
                  <div className="mobile-card-header">
                    <Tooltip content={formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)} longContent centerAboveTrigger>
                      <span className="mobile-card-servico" title={formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}>
                        {formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}
                      </span>
                    </Tooltip>
                    <MobileCardTotal>
                      {sobConsulta ? <ConsultaBadge>Consulta</ConsultaBadge> : formatBRL(total)}
                    </MobileCardTotal>
                  </div>
                  <div className="mobile-card-tag-profissional">
                    {s.modalidade ? <ModalidadeBadge>{s.modalidade}</ModalidadeBadge> : null}
                    <span>{s.profissional ?? "-"}</span>
                  </div>
                  {!sobConsulta && (
                    <>
                      <div className="mobile-card-creditos">Créditos: {s.creditos_padrao ?? "-"}</div>
                      <div className="mobile-card-valor-cr">Valor / Crédito: {formatBRL(s.valor_credito)}</div>
                    </>
                  )}
                  {editingPricing && (
                    <MobileCardEditRow>
                      {sobConsulta ? (
                        <>
                          <div className="mobile-edit-field"><span className="label">Créditos:</span> -</div>
                          <div className="mobile-edit-field"><span className="label">Valor / Crédito:</span> -</div>
                        </>
                      ) : (
                        <>
                          <PricingCellWithError className="mobile-align-start" $hasError={Boolean(pricingErrors[s.id_plano_servico_template]?.creditos_padrao)}>
                            <div className="mobile-edit-field">
                              <span className="label">Créditos:</span>
                              <InputSmall
                                type="number"
                                min={0}
                                value={s.creditos_padrao ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setDraftPricing((prev) =>
                                      prev.map((i) =>
                                        i.id_plano_servico_template === s.id_plano_servico_template
                                          ? { ...i, creditos_padrao: null }
                                          : i
                                      )
                                    );
                                    return;
                                  }
                                  const v = parseInt(raw, 10);
                                  if (!Number.isNaN(v) && v >= 0) {
                                    setDraftPricing((prev) =>
                                      prev.map((i) =>
                                        i.id_plano_servico_template === s.id_plano_servico_template
                                          ? { ...i, creditos_padrao: v }
                                          : i
                                      )
                                    );
                                  }
                                }}
                              />
                            </div>
                            {pricingErrors[s.id_plano_servico_template]?.creditos_padrao ? (
                              <FieldError>{pricingErrors[s.id_plano_servico_template].creditos_padrao}</FieldError>
                            ) : null}
                          </PricingCellWithError>
                          <PricingCellWithError className="mobile-align-start" $hasError={Boolean(pricingErrors[s.id_plano_servico_template]?.valor_credito)}>
                            <div className="mobile-edit-field">
                              <span className="label">Valor / Crédito:</span>
                              <InputMoneyCell>
                                <InputMoney
                                  value={s.valor_credito != null && !isNaN(Number(s.valor_credito)) ? Number(s.valor_credito).toFixed(2) : "0.00"}
                                  onChangeText={(v) => {
                                    const num = parseFloat(v);
                                    const safe = Number.isNaN(num) ? null : Math.max(0, num);
                                    setDraftPricing((prev) =>
                                      prev.map((i) =>
                                        i.id_plano_servico_template === s.id_plano_servico_template
                                          ? { ...i, valor_credito: safe }
                                          : i
                                      )
                                    );
                                  }}
                                  placeholder="R$ 0,00"
                                />
                              </InputMoneyCell>
                            </div>
                            {pricingErrors[s.id_plano_servico_template]?.valor_credito ? (
                              <FieldError>{pricingErrors[s.id_plano_servico_template].valor_credito}</FieldError>
                            ) : null}
                          </PricingCellWithError>
                        </>
                      )}
                      <div className="mobile-edit-field">
                        <span className="label">Sob Consulta</span>
                        <ToggleSwitch
                          type="button"
                          role="switch"
                          aria-checked={sobConsulta}
                          $on={sobConsulta}
                          onClick={() => handleToggleSobConsulta(s.id_plano_servico_template)}
                        >
                          <ToggleSwitchThumb $on={sobConsulta} />
                        </ToggleSwitch>
                      </div>
                    </MobileCardEditRow>
                  )}
                </MobileServiceCard>
              );
            })}
          </MobileCardList>
          <Table>
            <Thead>
              <Tr>
                <Th>Serviço</Th>
                <Th className="center">Modalidade</Th>
                <Th>Profissional</Th>
                <Th className="center">Créditos</Th>
                <Th className="center">Valor/Crédito</Th>
                <Th className="right">Valor Total</Th>
                {editingPricing && (
                  <Th className="right">
                    <span style={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
                      Sob Consulta
                      <Tooltip content="Quando ativado, o serviço fica marcado como Sob Consulta: não exige créditos nem valor fixo; o valor é definido sob consulta." side="left" longContent>
                        <span style={{ display: "inline-flex", marginLeft: 4, flexShrink: 0, cursor: "help" }} aria-label="Ajuda sobre Sob Consulta">
                          <HelpCircle size={12} color={comum.colors.interfaceGray400} />
                        </span>
                      </Tooltip>
                    </span>
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {displayList.map((s) => {
                const sobConsulta = s.modo_precificacao === "sob_consulta" || isOS;
                const cred = sobConsulta ? null : (s.creditos_padrao ?? 0);
                const val = sobConsulta ? null : (s.valor_credito ?? 0);
                const total = cred != null && val != null ? cred * val : null;
                return (
                  <Tr key={s.id_plano_servico_template}>
                    <Td>
                      {s.descricao?.trim() ? (
                        <Tooltip content={s.descricao.trim()} longContent centerAboveTrigger alignAbove="start">
                          <span style={{ cursor: "help" }}>
                            {formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}
                          </span>
                        </Tooltip>
                      ) : (
                        formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)
                      )}
                    </Td>
                    <Td className="center">
                      {s.modalidade ? <ModalidadeBadge>{s.modalidade}</ModalidadeBadge> : "-"}
                    </Td>
                    <Td>{s.profissional ?? "-"}</Td>
                    {editingPricing ? (
                      <>
                        <Td className="center">
                          {sobConsulta ? (
                            "-"
                          ) : (
                            <PricingCellWithError $hasError={Boolean(pricingErrors[s.id_plano_servico_template]?.creditos_padrao)}>
                              <InputSmall
                                type="number"
                                min={0}
                                value={s.creditos_padrao ?? ""}
                                onChange={(e) => {
                                  const raw = e.target.value;
                                  if (raw === "") {
                                    setDraftPricing((prev) => prev.map((i) => i.id_plano_servico_template === s.id_plano_servico_template ? { ...i, creditos_padrao: null } : i));
                                    setPricingErrors((prev) => {
                                      const next = { ...prev };
                                      if (next[s.id_plano_servico_template]) {
                                        delete next[s.id_plano_servico_template].creditos_padrao;
                                        if (Object.keys(next[s.id_plano_servico_template]).length === 0) delete next[s.id_plano_servico_template];
                                      }
                                      return next;
                                    });
                                    return;
                                  }
                                  const v = parseInt(raw, 10);
                                  if (Number.isNaN(v) || v < 0) return;
                                  setDraftPricing((prev) => prev.map((i) => i.id_plano_servico_template === s.id_plano_servico_template ? { ...i, creditos_padrao: v } : i));
                                  setPricingErrors((prev) => {
                                    const next = { ...prev };
                                    if (next[s.id_plano_servico_template]) {
                                      delete next[s.id_plano_servico_template].creditos_padrao;
                                      if (Object.keys(next[s.id_plano_servico_template]).length === 0) delete next[s.id_plano_servico_template];
                                    }
                                    return next;
                                  });
                                }}
                              />
                              {pricingErrors[s.id_plano_servico_template]?.creditos_padrao ? (
                                <FieldError>{pricingErrors[s.id_plano_servico_template].creditos_padrao}</FieldError>
                              ) : null}
                            </PricingCellWithError>
                          )}
                        </Td>
                        <Td className="center">
                          {sobConsulta ? (
                            "-"
                          ) : (
                            <PricingCellWithError $hasError={Boolean(pricingErrors[s.id_plano_servico_template]?.valor_credito)}>
                              <InputMoneyCell>
                                <InputMoney
                                  value={s.valor_credito != null && !isNaN(Number(s.valor_credito)) ? Number(s.valor_credito).toFixed(2) : "0.00"}
                                  onChangeText={(v) => {
                                    const num = parseFloat(v);
                                    const safe = Number.isNaN(num) ? null : Math.max(0, num);
                                    setDraftPricing((prev) => prev.map((i) => i.id_plano_servico_template === s.id_plano_servico_template ? { ...i, valor_credito: safe } : i));
                                    setPricingErrors((prev) => {
                                      const next = { ...prev };
                                      if (next[s.id_plano_servico_template]) {
                                        delete next[s.id_plano_servico_template].valor_credito;
                                        if (Object.keys(next[s.id_plano_servico_template]).length === 0) delete next[s.id_plano_servico_template];
                                      }
                                      return next;
                                    });
                                  }}
                                  placeholder="R$ 0,00"
                                />
                              </InputMoneyCell>
                              {pricingErrors[s.id_plano_servico_template]?.valor_credito ? (
                                <FieldError>{pricingErrors[s.id_plano_servico_template].valor_credito}</FieldError>
                              ) : null}
                            </PricingCellWithError>
                          )}
                        </Td>
                        <Td className="right">
                          {sobConsulta ? <ConsultaBadge>Consulta</ConsultaBadge> : <TotalValue>{formatBRL(total)}</TotalValue>}
                        </Td>
                        <Td className="right">
                          <ToggleSwitch
                            type="button"
                            role="switch"
                            aria-checked={sobConsulta}
                            $on={sobConsulta}
                            onClick={() => handleToggleSobConsulta(s.id_plano_servico_template)}
                          >
                            <ToggleSwitchThumb $on={sobConsulta} />
                          </ToggleSwitch>
                        </Td>
                      </>
                    ) : (
                      <>
                        <Td className="center">{sobConsulta ? "-" : (s.creditos_padrao ?? "-")}</Td>
                        <Td className="center">{sobConsulta ? "-" : formatBRL(s.valor_credito)}</Td>
                        <Td className="right">
                          {sobConsulta ? <ConsultaBadge>Consulta</ConsultaBadge> : <TotalValue>{formatBRL(total)}</TotalValue>}
                        </Td>
                      </>
                    )}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
            </>
          )}
        </TableWrap>
      </Card>

        </>
      )}

      {activeTab === "historico" && (
        <AbaHistoricoPlano abaAtiva={activeTab} planoId={planId} loading={loading} />
      )}
        </ContentCol>
      </Content>

      <ModalConfirmDiscard
        open={showConfirmDiscard}
        onClose={handleCloseConfirmDiscard}
        onDiscard={handleConfirmDiscard}
      />
    </Page>
  );
}
